import * as React from "react";
import {
  homeLink,
  linksOf,
  nameOfKind,
  parentLink,
  request,
  resourceLink,
  TerraformExporterResponse,
} from "../../../services/cpln";
import { Alert, Button, Checkbox, Input, Modal, notification, Select, Switch } from "antd";
import { ExportWithItemsColumn } from "./column";
import { clearItem, yamlDumpAll, k8sKeySort, toSortedJSON } from "../../../services/utils";
import { confirmSecretReveal } from "../../../utils/confirmSecretReveal";
import { LoaderSmall } from "../../../components/layout/loader/small";
import "./index.scss";
import { convertAllLinksToRelative } from "../../../utils/linkParser/linkParser";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { TerraformImportModal } from "../../../components/modals/tfImportModal";
import { Tooltip } from "../../../components/Tooltip";
import { WhatIsSlim } from "../../../utils/constants";
import { Workload } from "../../../schema/types/workload/entity";
import { Identity } from "../../../schema/types/identity/entity";
import { VolumeSet } from "../../../schema/types/volumeSet/entity";
import { Domain } from "../../../schema/types/domain/entity";
import { sortBy } from "lodash";
import { K8S_CRD_DEFAULT_NAMESPACE } from "../../../components/modals/viewModal";
import { CloudAccount } from "../../../schema/types/cloudaccount/entity";
import { Gvc } from "../../../schema/types/gvc/entity";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { Policy } from "../../../schema/types/policy/entity";
import { sentryLog } from "../../../tools/sentry";

interface Props {
  gvc: Gvc;
  onClose: () => void;
}

type ExporterFormat = "tf" | "k8s-crd";
type Formats = "json" | "yaml" | ExporterFormat;
const exporterFormats: ExporterFormat[] = ["tf", "k8s-crd"];

export const ExportWithItemsModal: React.FC<Props> = ({ gvc, onClose }) => {
  const org = useAppSelector(selectOrg);

  const [exportConfirmed, setExportConfirmed] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const [format, setFormat] = React.useState<Formats>("yaml");
  const [slim, setSlim] = React.useState<boolean>(true);

  const [workloads, setWorkloads] = React.useState<Workload[]>([]);
  const [workloadsFetched, setWorkloadsFetched] = React.useState<boolean>(false);
  const [selectedWorkloadLinks, setSelectedWorkloadLinks] = React.useState<string[]>([]);

  const [identities, setIdentities] = React.useState<Identity[]>([]);
  const [identitiesFetched, setIdentitiesFetched] = React.useState<boolean>(false);
  const [selectedIdentityLinks, setSelectedIdentityLinks] = React.useState<string[]>([]);
  const [requiredIdentityLinks, setRequiredIdentityLinks] = React.useState<string[]>([]);

  const [volumesets, setVolumesets] = React.useState<VolumeSet[]>([]);
  const [volumesetsFetched, setVolumesetsFetched] = React.useState<boolean>(false);
  const [selectedVolumesetLinks, setSelectedVolumesetLinks] = React.useState<string[]>([]);
  const [requiredVolumesetLinks, setRequiredVolumesetLinks] = React.useState<string[]>([]);

  const [domains, setDomains] = React.useState<Domain[]>([]);
  const [domainsFetched, setDomainsFetched] = React.useState<boolean>(false);

  const [policies, setPolicies] = React.useState<Policy[]>([]);
  const [policiesFetched, setPoliciesFetched] = React.useState<boolean>(false);

  const [includeReferenceItems, setIncludeReferenceItems] = React.useState<boolean>(false);
  const [includeProviderSnippet, setIncludeProviderSnippet] = React.useState(true);

  const [terraformImportCommand, setTerraformImportCommand] = React.useState<string>("");

  const [k8sCrdNamespace, setK8sCrdNamespace] = React.useState<string>(K8S_CRD_DEFAULT_NAMESPACE);
  const [k8sCrdKeep, setK8sCrdKeep] = React.useState(false);

  const [isExporting, setIsExporting] = React.useState(false);
  const [isDoneExportingProcess, setIsDoneExportingProcess] = React.useState(false);
  const [failedLinks, setFailedLinks] = React.useState<string[]>([]);
  const [successLinks, setSuccessLinks] = React.useState<string[]>([]);
  const [allLinks, setAllLinks] = React.useState<string[]>([]);

  const selectedAndRequiredIdentityLinks = [...selectedIdentityLinks];
  for (let requiredIdentityLink of requiredIdentityLinks) {
    if (!selectedAndRequiredIdentityLinks.includes(requiredIdentityLink)) {
      selectedAndRequiredIdentityLinks.push(requiredIdentityLink);
    }
  }

  const selectedAndRequiredVolumesetLinks = [...selectedVolumesetLinks];
  for (let requiredVolumesetLink of requiredVolumesetLinks) {
    if (!selectedAndRequiredVolumesetLinks.includes(requiredVolumesetLink)) {
      selectedAndRequiredVolumesetLinks.push(requiredVolumesetLink);
    }
  }

  const isExporterFormat: boolean = exporterFormats.includes(format as ExporterFormat);

  React.useEffect(() => {
    if (exportConfirmed && terraformImportCommand.trim().length == 0) {
      onClose();
    }
  }, [exportConfirmed, terraformImportCommand]);

  React.useEffect(() => {
    Promise.all([getAllWorkloads(), getAllIdentities(), getAllVolumesets(), getAllDomains(), getAllPolicies()]);
  }, []);

  React.useEffect(() => {
    const _requiredIdentityLinks: string[] = [];
    const _requiredVolumesetLinks: string[] = [];
    for (let workloadLink of selectedWorkloadLinks) {
      const workload = workloads.find((w) => linksOf(w).self === workloadLink);
      if (!workload) {
        continue;
      }
      if (workload.spec?.identityLink && identities.some((i) => linksOf(i).self === workload.spec?.identityLink)) {
        _requiredIdentityLinks.push(workload.spec.identityLink);
      }
      for (const container of workload.spec?.containers || []) {
        for (const volume of container.volumes || []) {
          if (volume.uri.includes("cpln://volumeset/")) {
            let volumeName = "";
            try {
              volumeName = volume.uri.split("cpln://volumeset/")[1];
            } catch (e) {}
            if (volumeName) {
              _requiredVolumesetLinks.push(resourceLink("volumeset", volumeName));
            }
          }
        }
      }
    }

    setRequiredIdentityLinks(_requiredIdentityLinks);
    setRequiredVolumesetLinks(_requiredVolumesetLinks);
  }, [selectedWorkloadLinks]);

  function toggleWorkloadAll() {
    if (selectedWorkloadLinks.length !== workloads.length) {
      setSelectedWorkloadLinks(workloads.map((w) => linksOf(w).self!));
    } else {
      setSelectedWorkloadLinks([]);
    }
  }

  function toggleWorkloadLink(link: string) {
    const isChecked = selectedWorkloadLinks.includes(link);
    if (isChecked) {
      setSelectedWorkloadLinks((links) => links.filter((_link) => _link !== link));
    } else {
      setSelectedWorkloadLinks((links) => [...links, link]);
    }
  }

  function toggleIdentityAll() {
    if (selectedAndRequiredIdentityLinks.length !== identities.length) {
      setSelectedIdentityLinks(identities.map((i) => linksOf(i).self!));
    } else {
      setSelectedIdentityLinks([...requiredIdentityLinks]);
    }
  }

  function toggleIdentityLink(link: string) {
    const isChecked = selectedIdentityLinks.includes(link);
    if (isChecked) {
      setSelectedIdentityLinks((links) => links.filter((_link) => _link !== link));
    } else {
      setSelectedIdentityLinks((links) => [...links, link]);
    }
  }

  function toggleVolumesetAll() {
    if (selectedAndRequiredVolumesetLinks.length !== volumesets.length) {
      setSelectedVolumesetLinks(volumesets.map((v) => linksOf(v).self!));
    } else {
      setSelectedVolumesetLinks([...requiredVolumesetLinks]);
    }
  }

  function toggleVolumesetLink(link: string) {
    const isChecked = selectedVolumesetLinks.includes(link);
    if (isChecked) {
      setSelectedVolumesetLinks((links) => links.filter((_link) => _link !== link));
    } else {
      setSelectedVolumesetLinks((links) => [...links, link]);
    }
  }

  async function getAllWorkloads() {
    let nextLink: undefined | string = parentLink("workload");
    let _workloads: Workload[] = [];
    while (nextLink) {
      const { data: workloadsRes } = await request({ url: nextLink });
      _workloads = _workloads.concat(workloadsRes.items);
      nextLink = linksOf(workloadsRes).next;
    }
    setWorkloads(sortBy(_workloads, (w) => w.name));
    setWorkloadsFetched(true);
  }

  async function getReferenceItems() {
    if (!includeReferenceItems) return [];
    const agentLinks: string[] = [];
    const cloudaccountLinks: string[] = [];
    const domainLinks: string[] = [];
    const secretLinks: string[] = [];
    const ipsetLinks: string[] = [];
    const policyLinks: string[] = [];

    function extractAndSetSecretFromLink(link: string) {
      let secretName = "";
      try {
        const parts = link.split("/");
        const secretIndex = parts.indexOf("secret");
        if (secretIndex !== -1 && secretIndex < parts.length - 1) {
          let potentialSecretName = parts[secretIndex + 1];
          const dotIndex = potentialSecretName.indexOf(".");
          if (dotIndex !== -1) {
            secretName = potentialSecretName.substring(0, dotIndex);
          } else {
            secretName = potentialSecretName;
          }
        }
      } catch (e) {}
      if (secretName) {
        secretLinks.push(resourceLink("secret", secretName));
      }
    }

    for (let identityLink of selectedAndRequiredIdentityLinks) {
      const identity = identities.find((i) => linksOf(i).self === identityLink);
      if (!identity) {
        continue;
      }
      if (identity.aws) {
        cloudaccountLinks.push(identity.aws.cloudAccountLink);
      }
      if (identity.azure) {
        cloudaccountLinks.push(identity.azure.cloudAccountLink);
      }
      if (identity.ngs) {
        cloudaccountLinks.push(identity.ngs.cloudAccountLink);
      }
      for (const networkResource of identity.networkResources || []) {
        if (networkResource.agentLink) {
          agentLinks.push(networkResource.agentLink);
        }
      }
    }
    for (let cloudaccountLink of cloudaccountLinks) {
      try {
        const { data: cloudaccount } = await request<CloudAccount>({ url: cloudaccountLink });
        if ((cloudaccount.data as any)?.secretLink) {
          extractAndSetSecretFromLink((cloudaccount.data as any).secretLink);
        }
      } catch (e) {
        continue;
      }
    }

    for (let domain of domains) {
      if (domain.spec) {
        if (domain.spec.gvcLink === `/org/${org}/gvc/${gvc.name}`) {
          domainLinks.push(linksOf(domain).self!);
        }
      }
    }

    for (let workloadLink of selectedWorkloadLinks) {
      const workload = workloads.find((w) => linksOf(w).self === workloadLink);
      if (!workload) {
        continue;
      }
      for (const container of workload.spec?.containers || []) {
        for (const volume of container.volumes || []) {
          if (volume.uri.includes("cpln://secret/")) {
            extractAndSetSecretFromLink(volume.uri);
          }
        }
        for (const env of container.env || []) {
          if (env.value?.includes("cpln://secret/")) {
            extractAndSetSecretFromLink(env.value);
          }
        }
      }
      for (let domain of domains) {
        const domainLink = linksOf(domain).self!;
        if (domain.spec) {
          if (domain.spec.gvcLink === `/org/${org}/gvc/${gvc.name}`) {
            domainLinks.push(domainLink);
          }
          for (let port of domain.spec.ports || []) {
            for (let route of port.routes || []) {
              if (route.workloadLink === workloadLink) {
                domainLinks.push(domainLink);
              }
            }
          }
        }
      }
    }

    for (let env of gvc.spec?.env || []) {
      if (env.value) {
        extractAndSetSecretFromLink(env.value);
      }
    }

    for (let link of gvc.spec?.pullSecretLinks || []) {
      extractAndSetSecretFromLink(link);
    }

    if (!!gvc.spec.loadBalancer?.dedicated && !!gvc.spec.loadBalancer?.ipSet) {
      ipsetLinks.push(gvc.spec.loadBalancer?.ipSet);
    }

    for (const policy of policies) {
      const policyLink = linksOf(policy).self!;

      if (policy.targetKind !== "secret") {
        sentryLog("gvc export fetched policy which does not target secret", { "data.link": policyLink });
        continue;
      }

      if (policy.target === "all") {
        policyLinks.push(policyLink);
        continue;
      }

      let isAdded = false;
      for (const targetLink of policy.targetLinks || []) {
        if (secretLinks.includes(targetLink)) {
          policyLinks.push(policyLink);
          isAdded = true;
          break;
        }
      }
      if (isAdded) {
        continue;
      }

      if (policy.targetQuery) {
        let queryLink: undefined | string = homeLink("policy") + `/-query`;
        while (queryLink && !isAdded) {
          const { data: queryRes } = await request({ method: "post", url: queryLink, body: policy.targetQuery });
          for (const queriedTarget of queryRes.items) {
            const queriedTargetLink = linksOf(queriedTarget).self!;
            if (secretLinks.includes(queriedTargetLink)) {
              policyLinks.push(policyLink);
              isAdded = true;
              break;
            }
          }
          // _policies = _policies.concat();
          queryLink = linksOf(queryRes).next;
        }
      }
    }

    let resolvedSecretLinks: string[];
    if (secretLinks.length > 0) {
      const shouldReveal = await confirmSecretReveal({ okText: "Reveal & Export", cancelText: "Export Without Revealing" });
      resolvedSecretLinks = shouldReveal ? secretLinks.map((s) => s + "/-reveal") : secretLinks;
    } else {
      resolvedSecretLinks = [];
    }

    return Array.from(
      new Set([
        ...agentLinks,
        ...cloudaccountLinks,
        ...resolvedSecretLinks,
        ...domainLinks,
        ...ipsetLinks,
        ...policyLinks,
      ]),
    );
  }

  async function getAllIdentities() {
    let nextLink: undefined | string = parentLink("identity");
    let _identities: Identity[] = [];
    while (nextLink) {
      const { data: identitiesRes } = await request({ url: nextLink });
      _identities = _identities.concat(identitiesRes.items);
      nextLink = linksOf(identitiesRes).next;
    }
    setIdentities(sortBy(_identities, (i) => i.name));
    setIdentitiesFetched(true);
  }

  async function getAllVolumesets() {
    let nextLink: undefined | string = parentLink("volumeset");
    let _volumesets: VolumeSet[] = [];
    while (nextLink) {
      const { data: volumesetsRes } = await request({ url: nextLink });
      _volumesets = _volumesets.concat(volumesetsRes.items);
      nextLink = linksOf(volumesetsRes).next;
    }
    setVolumesets(sortBy(_volumesets, (v) => v.name));
    setVolumesetsFetched(true);
  }

  async function getAllDomains() {
    let nextLink: undefined | string = homeLink("domain");
    let _domains: Domain[] = [];
    while (nextLink) {
      const { data: domainsRes } = await request({ url: nextLink });
      _domains = _domains.concat(domainsRes.items);
      nextLink = linksOf(domainsRes).next;
    }
    setDomains(_domains);
    setDomainsFetched(true);
  }

  async function getAllPolicies() {
    let nextLink: undefined | string = homeLink("policy") + `/-query`;
    let _policies: Policy[] = [];
    let isInitialPage = true;
    while (nextLink) {
      const { data: policiesRes } = await request({
        method: isInitialPage ? "post" : "get",
        url: nextLink,
        body: {
          kind: "policy",
          spec: {
            match: "all",
            terms: [
              {
                op: "=",
                property: "targetKind",
                value: "secret",
              },
            ],
          },
        },
      });
      isInitialPage = false;
      _policies = _policies.concat(policiesRes.items);
      nextLink = linksOf(policiesRes).next;
    }

    setPolicies(_policies);
    setPoliciesFetched(true);
  }

  async function confirmExport() {
    try {
      setIsLoading(true);

      let items: any = [];
      const referenceItems = await getReferenceItems();

      switch (format) {
        case "tf":
          // Determine request option
          const queryParameters: string[] = ["type=withItems", "import=true"];

          if (includeReferenceItems) {
            queryParameters.push("dependencies=true");
          }

          // TODO also send referenced items
          // Determine request URL
          const terraformPath: string = `${resourceLink("gvc", gvc.name)}?${queryParameters.join("&")}`;

          // Determine request body
          const terraformBody: any = {
            workloads: workloads.filter((w) => selectedWorkloadLinks.includes(linksOf(w).self!)).map((w) => w.name),
            identities: identities.filter((i) => selectedIdentityLinks.includes(linksOf(i).self!)).map((i) => i.name),
            volumesets: volumesets.filter((v) => selectedVolumesetLinks.includes(linksOf(v).self!)).map((v) => v.name),
          };

          const { data: terraformData } = await request({
            service: "terraform-exporter",
            method: "post",
            url: terraformPath,
            body: terraformBody,
          });
          exportTerraform(terraformData);
          break;
        case "k8s-crd":
          // Determine request URL
          const k8sCrdPath: string = `/batch?org=${org}&namespace=${k8sCrdNamespace}&keep=${String(k8sCrdKeep)}`;

          // Determine request body
          const k8sCrdBody: any = {
            links: [
              resourceLink("gvc", gvc.name),
              ...referenceItems,
              ...selectedWorkloadLinks,
              ...selectedAndRequiredVolumesetLinks,
              ...selectedAndRequiredIdentityLinks,
            ],
          };

          const { data: k8sCrdData } = await request({
            service: "k8s-crd-exporter",
            method: "post",
            url: k8sCrdPath,
            body: k8sCrdBody,
          });
          exportK8sCrd(k8sCrdData);
          break;
        default:
          setIsExporting(true);
          const gvcLink = resourceLink("gvc", gvc.name);
          const { data: gvcItem } = await request({ service: "api", url: gvcLink });
          setSuccessLinks((links) => [...links, gvcLink]);

          items.push(gvcItem);
          const allLinks = [
            ...referenceItems,
            ...selectedAndRequiredVolumesetLinks,
            ...selectedAndRequiredIdentityLinks,
            ...selectedWorkloadLinks,
          ];

          setAllLinks([gvcLink, ...allLinks]);
          const promises: Promise<any>[] = allLinks.map((l) =>
            request({ service: "api", url: l })
              .then(({ data }) => {
                items.push(data);
                setSuccessLinks((links) => [...links, l]);
              })
              .catch((e) => {
                setFailedLinks((links) => [...links, l]);
              }),
          );

          await Promise.allSettled(promises);

          if (slim) {
            for (const item of items) {
              clearItem(item);
            }
          }
          if (format === "json") {
            await exportJSON(items);
          }
          if (format === "yaml") {
            await exportYaml(items);
          }

          setIsDoneExportingProcess(true);
          break;
      }

      setIsLoading(false);

      if (format === "tf") {
        setExportConfirmed(true);
      }
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  async function exportJSON(data: any) {
    try {
      const blob = JSON.stringify(toSortedJSON(convertAllLinksToRelative(data)), null, 2);
      autoDownloadWithElementA(blob, "json");
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  async function exportYaml(items: any) {
    try {
      const blob = yamlDumpAll(convertAllLinksToRelative(items), {
        indent: 2,
        noRefs: true,
        sortKeys: k8sKeySort,
      });
      autoDownloadWithElementA(blob, "yaml");
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  function exportTerraform(data: TerraformExporterResponse) {
    const output: string[] = [];

    // Include provider snippet if specified
    if (includeProviderSnippet) {
      output.push(data.providerSnippet);
    }

    // Appead Terraform resources to the output
    output.push(data.resources.join("\n\n"));

    setTerraformImportCommand(data.imports.join("\n"));
    autoDownloadWithElementA(convertAllLinksToRelative(output.join("\n")), "tf");
  }

  function exportK8sCrd(data: any) {
    autoDownloadWithElementA(yamlDumpAll(data.items, { indent: 2, noRefs: true }), "yaml");
  }

  function autoDownloadWithElementA(blobData: any, extension: "json" | "yaml" | "tf", filename?: string) {
    const file = new Blob([blobData], { type: `text/${extension}` });
    const href = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.style.display = "none";
    a.classList.add("cpln-temp-a");
    a.download = (filename || `${org}_${gvc.name}-with-items`) + "." + extension;
    a.href = href;
    a.click();
  }

  return (
    <>
      {exportConfirmed ? (
        <TerraformImportModal command={terraformImportCommand} onClose={onClose} />
      ) : (
        <>
          <Modal
            title={
              <div className="flex items-center justify-between">
                <div className="mr-4 text-lg">Export GVC with Items</div>
                <div className="flex items-center gap-3 text-sm">
                  <Tooltip title={WhatIsSlim} placement="bottomLeft">
                    <div className={`flex items-center`}>
                      <div>
                        <Switch
                          value={slim && !isExporterFormat}
                          disabled={isExporterFormat}
                          onChange={(checked) => setSlim(checked)}
                        />
                        <span>Slim</span>
                      </div>
                    </div>
                  </Tooltip>
                  <Select
                    value={format}
                    options={[
                      { value: "json", label: "JSON" },
                      { value: "yaml", label: "YAML" },
                      { value: "tf", label: "Terraform" },
                      { value: "k8s-crd", label: "K8s CRD" },
                    ]}
                    onChange={(value) => setFormat(value as any)}
                    style={{ width: 120 }}
                  />
                </div>
              </div>
            }
            open={true}
            onCancel={onClose}
            onOk={confirmExport}
            cancelButtonProps={{ type: "default", disabled: isLoading }}
            okButtonProps={{
              type: "primary",
              loading: isLoading || !workloadsFetched || !identitiesFetched || !volumesetsFetched,
              disabled: isLoading || !workloadsFetched || !identitiesFetched || !volumesetsFetched,
            }}
            cancelText={`Cancel`}
            okText={`Export`}
            maskClosable={false}
            destroyOnClose
            width={900}
          >
            {workloadsFetched && identitiesFetched && volumesetsFetched ? (
              <div className={"export-with-items-modal flex flex-col gap-3"}>
                {/* Checkboxes */}
                <div className="flex gap-5">
                  {/* Include Referenced Items */}
                  <div className="flex items-center">
                    <Checkbox
                      checked={includeReferenceItems}
                      onChange={(e) => setIncludeReferenceItems(e.target.checked)}
                      disabled={!domainsFetched}
                    >
                      <span>Include referenced items</span>
                    </Checkbox>
                    <InfoTooltip
                      title={[
                        "Secrets, Agents, Cloud Accounts, Domains",
                        !domainsFetched ? "Fetching reference items..." : "",
                      ]}
                    />
                  </div>

                  {/* Include Provider Snippet */}
                  {format == "tf" ? (
                    <Checkbox
                      checked={includeProviderSnippet}
                      onChange={(e) => setIncludeProviderSnippet(e.target.checked)}
                      disabled={!domainsFetched}
                    >
                      <span>Include Provider Snippet</span>
                    </Checkbox>
                  ) : null}
                  {/* Keep K8s CRDs */}
                  {format == "k8s-crd" ? (
                    <div className="flex items-center">
                      <Checkbox
                        checked={k8sCrdKeep}
                        onChange={(e) => setK8sCrdKeep(e.target.value)}
                        disabled={!domainsFetched}
                      >
                        <span>Keep</span>
                      </Checkbox>
                      <InfoTooltip
                        title="Enabling this will add the 'cpln.io/resource-policy: keep' annotation to the exported K8s CRD to prevent it from being deleted by the operator in case this resource is deleted on Kubernetes."
                        placement="rightTop"
                        arrow={{ pointAtCenter: true }}
                      />
                    </div>
                  ) : null}
                </div>

                {/* K8s CRD Namespace */}
                {format == "k8s-crd" ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center">
                      <div>Namespace</div>
                      <InfoTooltip
                        title="Specify the Kubernetes namespace for the K8s CRD format"
                        placement="rightTop"
                        arrow={{ pointAtCenter: true }}
                      />
                    </div>
                    <Input value={k8sCrdNamespace} onChange={(e) => setK8sCrdNamespace(e.target.value)} />
                  </div>
                ) : null}
                {/* Items to Export */}
                <div className="flex flex-col gap-2">
                  <div style={{ margin: "0.25rem 0" }}>Select GVC items to export.</div>
                  <div className="export-with-items-modal-columns">
                    <ExportWithItemsColumn
                      items={workloads.map((workload) => ({
                        value: workload.name,
                        checked: selectedWorkloadLinks.includes(linksOf(workload).self!),
                        onChange: () => toggleWorkloadLink(linksOf(workload).self!),
                      }))}
                      kind="workload"
                      selectedItemsLength={selectedWorkloadLinks.length}
                      toggleAllItems={toggleWorkloadAll}
                    />
                    <ExportWithItemsColumn
                      items={identities.map((identity) => {
                        const disabled = requiredIdentityLinks.includes(linksOf(identity).self!);
                        const onChange = disabled ? undefined : () => toggleIdentityLink(linksOf(identity).self!);
                        return {
                          value: identity.name,
                          disabled: disabled,
                          checked: selectedAndRequiredIdentityLinks.includes(linksOf(identity).self!),
                          onChange: onChange,
                        };
                      })}
                      kind="identity"
                      selectedItemsLength={selectedAndRequiredIdentityLinks.length}
                      toggleAllItems={toggleIdentityAll}
                    />
                    <ExportWithItemsColumn
                      items={volumesets.map((volumeset) => {
                        const disabled = requiredVolumesetLinks.includes(linksOf(volumeset).self!);
                        const onChange = disabled ? undefined : () => toggleVolumesetLink(linksOf(volumeset).self!);
                        return {
                          value: volumeset.name,
                          disabled: disabled,
                          checked: selectedAndRequiredVolumesetLinks.includes(linksOf(volumeset).self!),
                          onChange: onChange,
                        };
                      })}
                      kind="volumeset"
                      selectedItemsLength={selectedAndRequiredVolumesetLinks.length}
                      toggleAllItems={toggleVolumesetAll}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "360px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoaderSmall />
              </div>
            )}
          </Modal>
          {isExporting ? (
            <Modal
              open={isExporting}
              onCancel={() => {
                setIsExporting(false);
                onClose();
              }}
              maskClosable={false}
              footer={
                <div className="flex items-center justify-end gap-4">
                  <Button
                    type="primary"
                    disabled={!isDoneExportingProcess}
                    loading={!isDoneExportingProcess}
                    onClick={() => {
                      setIsExporting(false);
                      onClose();
                    }}
                  >
                    OK
                  </Button>
                </div>
              }
            >
              <div className="overflow-y-hidden" style={{ maxHeight: 600 }}>
                <div className="mb-2 text-lg">Exporting GVC and {allLinks.length - 1} Items</div>
                <div className="mb-4">
                  Processing {failedLinks.length + successLinks.length}/{allLinks.length}
                </div>
                {failedLinks.length > 0 ? (
                  <div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                    <div className="mb-2">Failed Items</div>
                    {failedLinks.map((l) => {
                      return (
                        <div
                          key={l}
                          style={{
                            color: `${"var(--color-danger)"}`,
                          }}
                        >
                          {l}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {isDoneExportingProcess ? <Alert className="mt-4" message="Export is done" /> : null}
              </div>
            </Modal>
          ) : null}
        </>
      )}
    </>
  );
};
